import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Suggestion } from "@/types/document";
import { CircleX, MoreHorizontal, Pencil } from "lucide-react";
import { useState } from "react";

interface BlockQuickActionsProps {
  visible: boolean;
  onPositive: () => void;
  onNeutral: () => void;
  onNegative: () => void;
  onClear: () => void;
  onEdit: () => void;
  suggestion?: Suggestion;
  onAcceptSuggestion?: () => void;
  onRejectSuggestion?: () => void;
}

export const BlockQuickActions = ({
  visible,
  onPositive,
  onNeutral,
  onNegative,
  onClear,
  onEdit,
}: BlockQuickActionsProps) => {
  const [isOpen, setIsOpen] = useState(false);

  if (!visible) return null;

  const handleAction = (action: () => void) => {
    action();
    setIsOpen(false);
  };

  return (
    <div className="absolute -top-1 -right-11 z-50 opacity-0 group-hover:opacity-100 transition-opacity ">
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <Button
            onClick={(e) => e.stopPropagation()}
            className="rounded-md border border-zinc-200 bg-white hover:bg-zinc-50 w-8 h-8 flex items-center justify-center"
          >
            <MoreHorizontal className="h-4 w-4 text-zinc-600" />
          </Button>
        </PopoverTrigger>
        <PopoverContent side="left" align="end" className="p-2 w-auto">
          <div className="flex items-center gap-2">
            {/* Positive */}
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleAction(onPositive);
                  }}
                >
                  <span className="block h-4 w-4 rounded-full bg-green-500" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Positive (A)</TooltipContent>
            </Tooltip>

            {/* Neutral */}
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleAction(onNeutral);
                  }}
                >
                  <span className="block h-4 w-4 rounded-full bg-zinc-500" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Neutral (S)</TooltipContent>
            </Tooltip>

            {/* Negative */}
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleAction(onNegative);
                  }}
                >
                  <span className="block h-4 w-4 rounded-full bg-red-500" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Negative (D)</TooltipContent>
            </Tooltip>

            {/* Clear sentiment and note */}
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleAction(onClear);
                  }}
                >
                  <CircleX className="h-5 w-5 text-zinc-600" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                Clear sentiment and note (Backspace)
              </TooltipContent>
            </Tooltip>

            {/* Edit note */}
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleAction(onEdit);
                  }}
                >
                  <Pencil className="h-5 w-5 text-zinc-700" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Edit note (N)</TooltipContent>
            </Tooltip>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
};
